import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useDeferredValue, useEffect, useState } from "react";
import { z } from "zod";
import { Spinner } from "@/components/spinner";
import { Input } from "@/components/ui/input";
import { highlightSegments } from "@/lib/highlight";
import { tools } from "@/lib/tools";
import { cn } from "@/lib/utils";

/**
 * Search text and the open position live in the URL so other screens can link
 * here — the chat's "Nachgeschlagen" line points at `?suche=2400&nummer=2400`.
 * `nummer` alone would open a row that the default first page may not contain,
 * which is why links set both.
 */
// The router JSON-encodes values that look numeric (`?nummer=%222400%22`) so
// they read back as strings; a hand-typed `?nummer=2400` still arrives as a
// number and is accepted here rather than failing the route.
const text = z.preprocess(
  (value) => (typeof value === "number" ? String(value) : value),
  z.string().optional(),
);
export const gozSearch = z.object({ suche: text, nummer: text });
export type GozSearch = z.infer<typeof gozSearch>;

export const Route = createFileRoute("/_authed/goz")({
  validateSearch: gozSearch,
  component: Goz,
});

/** One page of results; the summary line says when there are more. */
const PAGE = 20;

const euro = (value: number) => `${value.toFixed(2).replace(".", ",")} €`;

/**
 * The GOZ 2012 fee schedule.
 *
 * Reads the same `dental/goz` routes the agent's scripts call, so the screen
 * and the agent see one resource shape (`goz_position`, list pages).
 */
function Goz() {
  const { suche = "", nummer } = Route.useSearch();
  const navigate = Route.useNavigate();
  const selected = nummer ?? null;
  // The input keeps its own state and mirrors it into the URL: binding a
  // controlled input straight to router state lags a keystroke behind, because
  // navigation settles asynchronously. The effect picks up links followed
  // while this page is already open.
  const [query, setQueryState] = useState(suche);
  useEffect(() => setQueryState(suche), [suche]);
  const setQuery = (value: string) => {
    setQueryState(value);
    void navigate({ search: (prev) => ({ ...prev, suche: value || undefined }), replace: true });
  };
  const setSelected = (value: string | null) =>
    void navigate({ search: (prev) => ({ ...prev, nummer: value ?? undefined }), replace: true });
  const deferredQuery = useDeferredValue(query);
  const search = useQuery({
    ...tools.queryOptions("GET /api/dental/goz/positions", { query: deferredQuery, limit: PAGE }),
    placeholderData: keepPreviousData,
  });
  const data = search.data;

  return (
    <div className="mx-auto w-full max-w-4xl p-4 md:p-8">
      <div className="space-y-1">
        <h1 className="font-serif text-3xl tracking-tight">GOZ 2012</h1>
        <p className="text-muted-foreground text-sm">
          Gebührenordnung für Zahnärzte. Wählen Sie eine Leistung für Details und Gebühren.
        </p>
      </div>

      {/* Stays in reach while scrolling a long result list; the negative
          margins let its background span the page padding it sits in. */}
      <div className="sticky top-0 z-10 -mx-4 bg-background px-4 pt-4 pb-3 md:-mx-8 md:px-8">
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            className="h-10 pl-9 text-[15px]"
            placeholder="Nummer oder Stichwort, z. B. 0010 oder Wurzelkanal"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="GOZ durchsuchen"
          />
        </div>
        {data ? (
          <p aria-live="polite" className="mt-2 text-muted-foreground text-xs">
            {resultSummary(data.data.length, data.total, deferredQuery)}
          </p>
        ) : null}
      </div>

      {search.error ? (
        <p role="alert" className="text-destructive text-sm">
          Die Suche konnte nicht geladen werden. Bitte versuchen Sie es erneut.
        </p>
      ) : null}
      {search.isPending ? (
        <Spinner />
      ) : data && data.data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border">
          <div
            aria-hidden
            className="hidden grid-cols-[4.5rem_1fr_4.5rem] gap-x-4 border-b bg-muted/50 px-4 py-2 font-medium text-muted-foreground text-xs md:grid"
          >
            <span>Nr.</span>
            <span>Leistung</span>
            <span className="text-right">Punkte</span>
          </div>
          <ul className="divide-y" data-testid="goz-results">
            {data.data.map((item) => {
              const open = selected === item.number;
              return (
                <li key={item.number}>
                  <button
                    type="button"
                    onClick={() => setSelected(open ? null : item.number)}
                    aria-expanded={open}
                    aria-controls={`goz-detail-${item.number}`}
                    className={cn(
                      "grid w-full grid-cols-[1fr_auto] gap-x-4 gap-y-1 px-4 py-3 text-left transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none md:grid-cols-[4.5rem_1fr_4.5rem] md:items-baseline",
                      open && "bg-muted/60",
                    )}
                  >
                    <span className="col-start-1 row-start-1 font-medium font-mono text-sm md:col-start-auto md:row-start-auto">
                      {item.number}
                    </span>
                    {/* Below md the points sit beside the number and the
                        description wraps underneath at full width. */}
                    <span className="col-span-2 row-start-2 line-clamp-2 text-sm md:col-span-1 md:row-start-auto">
                      {highlightSegments(item.description, deferredQuery).map((segment, index) =>
                        segment.match ? (
                          // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional and re-derived together
                          <mark key={index} className="rounded-sm bg-accent/25 text-inherit">
                            {segment.text}
                          </mark>
                        ) : (
                          segment.text
                        ),
                      )}
                    </span>
                    <span className="col-start-2 row-start-1 text-right text-muted-foreground text-sm tabular-nums md:col-start-auto md:row-start-auto">
                      {item.points ?? "–"}
                    </span>
                  </button>
                  {open ? (
                    <div
                      id={`goz-detail-${item.number}`}
                      className="border-t bg-muted/30 px-4 py-4 md:pl-[6.5rem]"
                    >
                      <div className="space-y-4" data-testid="goz-detail">
                        <p className="text-sm leading-relaxed">{item.description}</p>
                        {item.amounts ? (
                          <dl className="grid max-w-md grid-cols-3 gap-2">
                            {factors.map(([key, label]) => (
                              <div key={key} className="rounded-lg border bg-background px-3 py-2">
                                <dt className="text-muted-foreground text-xs">{label}</dt>
                                <dd className="font-medium text-sm tabular-nums">
                                  {euro(item.amounts?.[key] ?? 0)}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        ) : null}
                        {item.constraints.length > 0 ? (
                          <ul className="space-y-1.5">
                            {item.constraints.map((constraint) => (
                              <li key={constraint.text} className="text-muted-foreground text-xs">
                                <span className="font-medium text-foreground">
                                  {constraintLabels[constraint.kind]}
                                </span>
                                : {constraint.text}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : data ? (
        <p className="py-8 text-center text-muted-foreground text-sm">
          Keine Treffer für „{deferredQuery}“.
        </p>
      ) : null}
    </div>
  );
}

const factors = [
  ["simple", "1,0-fach"],
  ["threshold", "2,3-fach"],
  ["max", "3,5-fach"],
] as const;

const constraintLabels = {
  not_combinable: "Nicht kombinierbar",
  once_per_case: "Einmal je Behandlungsfall",
  requires_justification: "Begründung erforderlich",
  material_separate: "Material gesondert",
  custom: "Hinweis",
} as const;

// A page holds `PAGE` results and the server reports the true total; saying so is what
// tells the user to narrow the search instead of assuming the list is complete.
function resultSummary(returned: number, total: number, query: string): string {
  if (!query.trim())
    return `${returned} von ${total} Leistungen – suchen Sie nach Nummer oder Stichwort`;
  if (returned < total) return `${returned} von ${total} Treffern – verfeinern Sie die Suche`;
  return total === 1 ? "1 Treffer" : `${total} Treffer`;
}
